import React, { useState, useEffect } from "react";
import axios from "../api/axios";
import {
  FiTrendingUp, FiCalendar, FiDollarSign, FiCheckCircle,
  FiClock, FiAlertCircle, FiRefreshCw, FiFilter, FiActivity
} from "react-icons/fi";

/* ═══════════════════════════════════════════
   STYLES — NEUMORPHISM WITH VIBRANT COLORS
═══════════════════════════════════════════ */
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap');

  .sp-root {
    font-family: 'Montserrat', sans-serif;
    background: linear-gradient(135deg, #e0e5ec 0%, #d0d5dc 100%);
    min-height: 100%;
  }
  .sp-root *, .sp-root *::before, .sp-root *::after { box-sizing: border-box; }
  .sp-root ::-webkit-scrollbar { width: 4px; height: 4px; }
  .sp-root ::-webkit-scrollbar-track { background: #e0e5ec; border-radius: 99px; }
  .sp-root ::-webkit-scrollbar-thumb { background: #3b82f6; border-radius: 99px; }

  .sp-page {
    max-width: 1280px; margin: 0 auto;
    padding: 24px 20px;
    display: flex; flex-direction: column; gap: 24px;
  }
  @media (max-width: 600px) { .sp-page { padding: 6px 5px; gap: 16px; } }

  /* ══════════════════════════════
     HERO - NEUMORPHISM
  ══════════════════════════════ */
  .sp-hero {
    background: linear-gradient(135deg, #1e3a8a 0%, #1e40af 50%, #2563eb 100%);
    border-radius: 9px;
    padding: 32px 36px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    flex-wrap: wrap;
    position: relative;
    overflow: hidden;
    box-shadow: 12px 12px 24px rgba(0, 0, 0, 0.2), -8px -8px 16px rgba(255, 255, 255, 0.1);
  }
  .sp-hero::before {
    content: ''; position: absolute; top: -80px; right: -60px;
    width: 280px; height: 280px; border-radius: 50%;
    background: radial-gradient(circle, rgba(251,191,36,.2) 0%, transparent 70%);
    pointer-events: none;
  }
  .sp-hero::after {
    content: ''; position: absolute; bottom: -40px; left: 30%;
    width: 180px; height: 180px; border-radius: 50%;
    background: radial-gradient(circle, rgba(59,130,246,.15) 0%, transparent 70%);
    pointer-events: none;
  }
  .sp-hero-left { position: relative; z-index: 1; }
  .sp-hero-badge {
    display: inline-flex; align-items: center; gap: 6px;
    background: rgba(251,191,36,.2);
    border: 1px solid rgba(251,191,36,.4);
    border-radius: 99px; padding: 5px 14px;
    font-size: 11px; font-weight: 700; color: #fcd34d;
    letter-spacing: .07em; text-transform: uppercase; margin-bottom: 12px;
    backdrop-filter: blur(10px);
  }
  .sp-badge-dot {
    width: 6px; height: 6px; border-radius: 50%; background: #fbbf24;
    animation: sp-pulse 2s infinite;
  }
  @keyframes sp-pulse { 0%,100%{opacity:1} 50%{opacity:.35} }
  .sp-hero-title {
    font-size: clamp(20px, 4vw, 28px); font-weight: 900;
    color: #fff; line-height: 1.2; margin-bottom: 6px;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
  }
  .sp-hero-sub { font-size: 13px; font-weight: 500; color: rgba(255, 255, 255, 0.8); }

  .sp-refresh-btn {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 12px 20px; border-radius: 30px; border: none; cursor: pointer;
    font-family: 'Montserrat', sans-serif; font-size: 13px; font-weight: 800;
    background: linear-gradient(135deg, #fbbf24, #f59e0b);
    color: #1e3a8a;
    box-shadow: 8px 8px 16px rgba(0, 0, 0, 0.2), -4px -4px 8px rgba(255, 255, 255, 0.1);
    transition: all 0.2s ease;
    position: relative; z-index: 1;
  }
  .sp-refresh-btn:hover {
    transform: translateY(-2px);
    box-shadow: 10px 10px 20px rgba(0, 0, 0, 0.25), -6px -6px 12px rgba(255, 255, 255, 0.15);
  }
  .sp-refresh-btn:active {
    transform: translateY(2px);
    box-shadow: inset 4px 4px 8px rgba(0, 0, 0, 0.2), inset -4px -4px 8px rgba(255, 255, 255, 0.1);
  }
  .sp-refresh-btn.spinning svg {
    animation: sp-spin .7s linear infinite;
  }
  @keyframes sp-spin {
    to { transform: rotate(360deg); }
  }

  /* ══════════════════════════════
     SUMMARY CARDS - NEUMORPHISM
  ══════════════════════════════ */
  .sp-summary {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 20px;
    align-items: stretch;
    max-width: 980px;
    margin: 0 auto;
  }
  /* Keep two cards per row on all screen sizes */

  .sp-stat-card {
    border-radius: 9px;
    padding: 22px;
    transition: all 0.2s ease;
    position: relative;
  }
  .sp-stat-card:active {
    transform: translateY(2px);
  }
  
  /* Individual card colors */
  .sp-stat-card.blue {
    background: linear-gradient(135deg, #eff6ff, #dbeafe);
    box-shadow: 12px 12px 24px rgba(59, 130, 246, 0.2), -8px -8px 16px rgba(255, 255, 255, 0.5);
  }
  .sp-stat-card.green {
    background: linear-gradient(135deg, #f0fdf4, #dcfce7);
    box-shadow: 12px 12px 24px rgba(16, 185, 129, 0.2), -8px -8px 16px rgba(255, 255, 255, 0.5);
  }
  .sp-stat-card.purple {
    background: linear-gradient(135deg, #f5f3ff, #ede9fe);
    box-shadow: 12px 12px 24px rgba(139, 92, 246, 0.2), -8px -8px 16px rgba(255, 255, 255, 0.5);
  }
  .sp-stat-card.amber {
    background: linear-gradient(135deg, #fffbeb, #fef3c7);
    box-shadow: 12px 12px 24px rgba(245, 158, 11, 0.2), -8px -8px 16px rgba(255, 255, 255, 0.5);
  }

  .sp-stat-top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 14px;
  }
  .sp-stat-icon {
    width: 28px;
    height: 28px;
    border-radius: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .sp-stat-card.blue .sp-stat-icon {
    background: #3b82f6;
    color: white;
    box-shadow: 6px 6px 12px rgba(59, 130, 246, 0.3), -4px -4px 8px rgba(255, 255, 255, 0.5);
  }
  .sp-stat-card.green .sp-stat-icon {
    background: #10b981;
    color: white;
    box-shadow: 6px 6px 12px rgba(16, 185, 129, 0.3), -4px -4px 8px rgba(255, 255, 255, 0.5);
  }
  .sp-stat-card.purple .sp-stat-icon {
    background: #8b5cf6;
    color: white;
    box-shadow: 6px 6px 12px rgba(139, 92, 246, 0.3), -4px -4px 8px rgba(255, 255, 255, 0.5);
  }
  .sp-stat-card.amber .sp-stat-icon {
    background: #f59e0b;
    color: white;
    box-shadow: 6px 6px 12px rgba(245, 158, 11, 0.3), -4px -4px 8px rgba(255, 255, 255, 0.5);
  }

  .sp-stat-value {
    font-size: clamp(15px, 2.8vw, 26px);
    font-weight: 600;
    text-align: right;
  }
  .sp-stat-card.blue .sp-stat-value { color: #1e3a8a; }
  .sp-stat-card.green .sp-stat-value { color: #064e3b; }
  .sp-stat-card.purple .sp-stat-value { color: #4c1d95; }
  .sp-stat-card.amber .sp-stat-value { color: #78350f; }
  
  .sp-stat-label {
    font-size: 11px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: .07em;
  }
  .sp-stat-card.blue .sp-stat-label { color: #1e40af; }
  .sp-stat-card.green .sp-stat-label { color: #065f46; }
  .sp-stat-card.purple .sp-stat-label { color: #5b21b6; }
  .sp-stat-card.amber .sp-stat-label { color: #92400e; }

  /* ══════════════════════════════
     SECTION LABEL
  ══════════════════════════════ */
  .sp-section-label {
    font-size: 11px;
    font-weight: 800;
    color: #4b5563;
    text-transform: uppercase;
    letter-spacing: .09em;
    margin-bottom: -6px;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .sp-section-label::after {
    content: '';
    flex: 1;
    height: 2px;
    background: linear-gradient(90deg, #3b82f6, transparent);
    border-radius: 99px;
  }

  /* ══════════════════════════════
     CONTROLS BAR - NEUMORPHISM
  ══════════════════════════════ */
  .sp-controls {
    background: #e8edf5;
    border-radius: 28px;
    padding: 18px 24px;
    box-shadow: 12px 12px 24px rgba(0, 0, 0, 0.1), -8px -8px 16px rgba(255, 255, 255, 0.5);
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
  }
  .sp-filter-label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 800;
    color: #4b5563;
    white-space: nowrap;
  }
  .sp-filter-btns {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
  }
  .sp-filter-btn {
    padding: 8px 20px;
    border-radius: 40px;
    border: none;
    background: #e8edf5;
    cursor: pointer;
    font-family: 'Montserrat', sans-serif;
    font-size: 12px;
    font-weight: 700;
    color: #6b7280;
    transition: all 0.2s ease;
    box-shadow: 6px 6px 12px rgba(0, 0, 0, 0.08), -4px -4px 8px rgba(255, 255, 255, 0.5);
    white-space: nowrap;
  }
  .sp-filter-btn:hover {
    transform: translateY(-1px);
    color: #3b82f6;
    box-shadow: 8px 8px 16px rgba(0, 0, 0, 0.1), -6px -6px 12px rgba(255, 255, 255, 0.6);
  }
  .sp-filter-btn:active {
    transform: translateY(2px);
    box-shadow: inset 4px 4px 8px rgba(0, 0, 0, 0.08), inset -4px -4px 8px rgba(255, 255, 255, 0.5);
  }
  .sp-filter-btn.active {
    background: linear-gradient(135deg, #3b82f6, #2563eb);
    color: white;
    box-shadow: inset 4px 4px 8px rgba(0, 0, 0, 0.2), inset -4px -4px 8px rgba(255, 255, 255, 0.1);
  }

  /* ══════════════════════════════
     ALERT - NEUMORPHISM
  ══════════════════════════════ */
  .sp-alert {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 16px 20px;
    border-radius: 24px;
    background: linear-gradient(135deg, #fef2f2, #fee2e2);
    font-size: 13px;
    font-weight: 600;
    color: #b91c1c;
    box-shadow: inset 4px 4px 8px rgba(0, 0, 0, 0.05), inset -4px -4px 8px rgba(255, 255, 255, 0.5);
  }
  .sp-alert svg { flex-shrink: 0; margin-top: 2px; }

  /* ══════════════════════════════
     TABLE CARD - NEUMORPHISM
  ══════════════════════════════ */
  .sp-table-card {
    background: #e8edf5;
    border-radius: 28px;
    box-shadow: 12px 12px 24px rgba(0, 0, 0, 0.1), -8px -8px 16px rgba(255, 255, 255, 0.5);
    overflow: hidden;
  }
  .sp-table-wrap { overflow-x: auto; }
  .sp-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
    min-width: 900px;
  }
  .sp-table thead tr {
    background: linear-gradient(135deg, #1e3a8a, #1e40af);
  }
  .sp-table thead th {
    padding: 16px 18px;
    text-align: left;
    font-size: 10px;
    font-weight: 800;
    color: rgba(255, 255, 255, 0.8);
    text-transform: uppercase;
    letter-spacing: .08em;
    white-space: nowrap;
  }
  .sp-table tbody tr {
    border-bottom: 1px solid rgba(59, 130, 246, 0.15);
    transition: all .15s;
  }
  .sp-table tbody tr:last-child { border-bottom: none; }
  .sp-table tbody tr:hover {
    background: rgba(59, 130, 246, 0.05);
    transform: scale(1.01);
  }
  .sp-table td { padding: 18px; vertical-align: middle; }

  /* Plan details cell */
  .sp-plan-name {
    font-size: 14px;
    font-weight: 800;
    color: #1f2937;
    margin-bottom: 4px;
  }
  .sp-plan-meta {
    font-size: 11.5px;
    font-weight: 600;
    color: #6b7280;
  }

  /* Investment cell */
  .sp-invest-amount {
    font-size: 15px;
    font-weight: 900;
    color: #1f2937;
    margin-bottom: 2px;
  }
  .sp-invest-label {
    font-size: 10px;
    font-weight: 700;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: .06em;
  }

  /* ROI cell */
  .sp-roi-return {
    font-size: 15px;
    font-weight: 900;
    color: #059669;
    margin-bottom: 3px;
  }
  .sp-roi-pct {
    font-size: 11.5px;
    font-weight: 700;
    color: #6b7280;
    margin-bottom: 2px;
  }
  .sp-roi-weekly {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 10px;
    border-radius: 20px;
    background: linear-gradient(135deg, #fffbeb, #fef3c7);
    font-size: 11px;
    font-weight: 800;
    color: #92400e;
    box-shadow: inset 2px 2px 4px rgba(0, 0, 0, 0.05), inset -2px -2px 4px rgba(255, 255, 255, 0.5);
  }

  /* Progress cell */
  .sp-progress-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 6px;
  }
  .sp-progress-weeks {
    font-size: 12px;
    font-weight: 700;
    color: #374151;
  }
  .sp-progress-pct {
    font-size: 12px;
    font-weight: 800;
    color: #1f2937;
  }
  .sp-progress-track {
    height: 8px;
    background: #dce3ec;
    border-radius: 99px;
    overflow: hidden;
    margin-bottom: 6px;
    box-shadow: inset 2px 2px 4px rgba(0, 0, 0, 0.05);
  }
  .sp-progress-fill {
    height: 100%;
    border-radius: 99px;
    background: linear-gradient(90deg, #fbbf24, #f59e0b);
    transition: width .6s cubic-bezier(.4, 0, .2, 1);
  }
  .sp-progress-fill.done {
    background: linear-gradient(90deg, #10b981, #059669);
  }
  .sp-progress-paid {
    font-size: 11px;
    font-weight: 600;
    color: #6b7280;
    margin-top: 4px;
  }

  /* Status cell */
  .sp-status {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 14px;
    border-radius: 99px;
    font-size: 11px;
    font-weight: 800;
    box-shadow: inset 2px 2px 4px rgba(0, 0, 0, 0.05), inset -2px -2px 4px rgba(255, 255, 255, 0.5);
  }
  .sp-status.active {
    background: linear-gradient(135deg, #dcfce7, #bbf7d0);
    color: #15803d;
  }
  .sp-status.completed {
    background: linear-gradient(135deg, #dbeafe, #bfdbfe);
    color: #1d4ed8;
  }
  .sp-status.pending {
    background: linear-gradient(135deg, #fef9c3, #fde68a);
    color: #a16207;
  }
  .sp-status.default {
    background: #dce3ec;
    color: #6b7280;
  }

  /* Dates cell */
  .sp-date-main {
    font-size: 12.5px;
    font-weight: 700;
    color: #1f2937;
    margin-bottom: 3px;
  }
  .sp-date-label {
    font-size: 10px;
    font-weight: 700;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: .06em;
  }

  /* Empty state */
  .sp-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    text-align: center;
    background: #e8edf5;
    border-radius: 20px;
    margin: 20px;
  }
  .sp-empty-icon {
    font-size: 52px;
    margin-bottom: 16px;
  }
  .sp-empty-title {
    font-size: 16px;
    font-weight: 800;
    color: #1f2937;
    margin-bottom: 8px;
  }
  .sp-empty-sub {
    font-size: 13px;
    font-weight: 500;
    color: #6b7280;
    max-width: 340px;
  }

  /* ══════════════════════════════
     MOBILE CARD VIEW (< 700px)
  ══════════════════════════════ */
  .sp-cards-list {
    display: none;
    flex-direction: column;
    gap: 16px;
    padding: 20px;
  }
  @media (max-width: 799px) {
    .sp-table-wrap { display: none; }
    .sp-cards-list { display: flex; }
  }

  .sp-purchase-card {
    background: linear-gradient(135deg, #ffffff, #f3f6fc);
    border-radius: 9px;
    padding: 20px;
    position: relative;
    overflow: hidden;
    box-shadow: 12px 12px 24px rgba(0, 0, 0, 0.1), -8px -8px 16px rgba(255, 255, 255, 0.5);
    transition: all 0.2s ease;
  }
  .sp-purchase-card:active {
    transform: translateY(2px);
    box-shadow: inset 8px 8px 16px rgba(0, 0, 0, 0.08), inset -8px -8px 16px rgba(255, 255, 255, 0.5);
  }
  .sp-purchase-card::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
  }
  .sp-purchase-card.active::before {
    background: linear-gradient(180deg, #10b981, #059669);
  }
  .sp-purchase-card.completed::before {
    background: linear-gradient(180deg, #3b82f6, #2563eb);
  }
  .sp-purchase-card.pending::before {
    background: linear-gradient(180deg, #fbbf24, #f59e0b);
  }

  .sp-card-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 16px;
    gap: 10px;
  }
  .sp-card-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
    margin-bottom: 16px;
  }
  .sp-card-item-label {
    font-size: 9px;
    font-weight: 800;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: .08em;
    margin-bottom: 4px;
  }
  .sp-card-item-val {
    font-size: 14px;
    font-weight: 900;
    color: #1f2937;
  }
  .sp-card-item-sub {
    font-size: 11px;
    font-weight: 600;
    color: #6b7280;
    margin-top: 2px;
  }

  /* ══════════════════════════════
     SKELETON LOADING
  ══════════════════════════════ */
  .sp-skeleton {
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 24px;
  }
  .sp-skel {
    border-radius: 28px;
    background: linear-gradient(135deg, #e8edf5, #dce3ec);
    position: relative;
    overflow: hidden;
    box-shadow: inset 3px 3px 6px rgba(0, 0, 0, 0.05), inset -3px -3px 6px rgba(255, 255, 255, 0.5);
  }
  .sp-skel::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent 25%, rgba(59, 130, 246, 0.1) 50%, transparent 75%);
    background-size: 200% 100%;
    animation: sp-shimmer 1.4s infinite;
  }
  @keyframes sp-shimmer {
    from { background-position: -200% 0; }
    to { background-position: 200% 0; }
  }
  .sp-skel-hero { height: 130px; }
  .sp-skel-row {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 20px;
  }
  @media (max-width: 900px) {
    .sp-skel-row { grid-template-columns: repeat(2, 1fr); }
  }
  .sp-skel-box {
    height: 120px;
    border-radius: 28px;
    background: linear-gradient(135deg, #e8edf5, #dce3ec);
    position: relative;
    overflow: hidden;
  }
  .sp-skel-tbl { height: 320px; }
`;

/* ═══════════════════════════════════════════
   HELPERS
═══════════════════════════════════════════ */
const fmtDate = (d) => new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
const fmtCur = (n) => `₹${Number(n).toLocaleString("en-IN")}`;

const statusClass = (s) => {
  if (s === "active") return "active";
  if (s === "completed") return "completed";
  if (s === "pending") return "pending";
  return "default";
};

const StatusPill = ({ status }) => {
  const icons = {
    active: <FiTrendingUp size={11} />,
    completed: <FiCheckCircle size={11} />,
    pending: <FiClock size={11} />,
  };
  return (
    <span className={`sp-status ${statusClass(status)}`}>
      {icons[status] || <FiAlertCircle size={11} />}
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

/* ═══════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════ */
const SharePurchases = ({ user }) => {
  const [purchases, setPurchases] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [spinning, setSpinning] = useState(false);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");

  const fetchSharePurchases = async (fromBtn = false) => {
    try {
      if (fromBtn) setSpinning(true);
      setLoading(true);
      const { data } = await axios.get("/share/my-purchases");
      setPurchases(data.purchases);
      setSummary(data.summary);
      setError("");
    } catch (err) {
      setError("Failed to load share purchases. Please try again.");
      console.error(err);
    } finally {
      setLoading(false);
      if (fromBtn) setTimeout(() => setSpinning(false), 700);
    }
  };

  useEffect(() => { fetchSharePurchases(); }, []);

  const filtered = purchases.filter(p => filter === "all" || p.status === filter);

  if (loading) return (
    <div className="sp-root">
      <style>{styles}</style>
      <div className="sp-skeleton">
        <div className="sp-skel sp-skel-hero" />
        <div className="sp-skel-row">{[1, 2, 3, 4].map(i => <div className="sp-skel-box" key={i} />)}</div>
        <div className="sp-skel sp-skel-tbl" />
      </div>
    </div>
  );

  return (
    <div className="sp-root">
      <style>{styles}</style>
      <div className="sp-page">

        {/* Hero */}
        <div className="sp-hero">
          <div className="sp-hero-left">
            <div className="sp-hero-badge">
              <div className="sp-badge-dot" />
              GCP Investments
            </div>
            <div className="sp-hero-title">Growth Capital Partner Income History</div>
            <div className="sp-hero-sub">Track your share investments and EMI progress</div>
          </div>
          <button
            className={`sp-refresh-btn ${spinning ? "spinning" : ""}`}
            onClick={() => fetchSharePurchases(true)}
          >
            <FiRefreshCw size={14} /> Refresh
          </button>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="sp-summary">
            <StatCard variant="blue" icon={<FiTrendingUp size={18} />} label="Total Loan Buy" value={summary.totalPurchases} />
            <StatCard variant="green" icon={<FiDollarSign size={18} />} label="Total Loan pay" value={fmtCur(summary.totalInvested)} />
            <StatCard variant="purple" icon={<FiCheckCircle size={18} />} label="Active Loan" value={summary.activePurchases} />
            <StatCard variant="amber" icon={<FiCalendar size={18} />} label="Total Loan Profit" value={fmtCur(summary.totalPaid)} />
          </div>
        )}

        {/* Controls */}
        <div className="sp-controls">
          <div className="sp-filter-label">
            <FiFilter size={13} /> Filter by status
          </div>
          <div className="sp-filter-btns">
            {["all", "active", "completed"].map(s => (
              <button
                key={s}
                className={`sp-filter-btn ${filter === s ? "active" : ""}`}
                onClick={() => setFilter(s)}
              >
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
          <div style={{ marginLeft: "auto", fontSize: 12, fontWeight: 700, color: "#6b7280" }}>
            <strong style={{ color: "#3b82f6" }}>{filtered.length}</strong> of {purchases.length} records
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="sp-alert">
            <FiAlertCircle size={15} /> {error}
          </div>
        )}

        {/* Table Section */}
        <div className="sp-section-label">Investment History</div>
        <div className="sp-table-card">
          {/* Desktop table */}
          <div className="sp-table-wrap">
            <table className="sp-table">
              <thead>
                <tr>
                  <th>Plan Details</th>
                  <th>Investment</th>
                  <th>EMI Details</th>
                  <th>Progress</th>
                  <th>Status</th>
                  <th>Next Payout</th>
                  <th>Dates</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan="7">
                      <div className="sp-empty">
                        <div className="sp-empty-icon">📈</div>
                        <div className="sp-empty-title">No share purchases found</div>
                        <div className="sp-empty-sub">
                          {filter === "all"
                            ? "You haven't purchased any shares yet"
                            : `No ${filter} share purchases found`}
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : filtered.map(p => {
                  const pct = Math.max(0, Math.min(100, Number(p.progressPercentage || 0)));
                  return (
                    <tr key={p._id}>
                      {/* Plan */}
                      <td>
                        <div className="sp-plan-name">{p.planName}</div>
                      </td>
                      {/* Investment */}
                      <td>
                        <div className="sp-invest-amount">{fmtCur(p.amount)}</div>
                        <div className="sp-invest-label">Loan Amount</div>
                      </td>
                      {/* ROI */}
                      <td>
                        <div className="sp-roi-return">{fmtCur(p.totalReturn)}</div>
                        <div className="sp-roi-pct">{p.roiPercentage}% Yearly</div>
                        <span className="sp-roi-weekly">
                          <FiCalendar size={10} /> {fmtCur(p.weeklyPayout)}/wk
                        </span>
                      </td>
                      {/* Progress */}
                      <td style={{ minWidth: 180 }}>
                        <div className="sp-progress-header">
                          <span className="sp-progress-weeks">{p.weeksPaid}/{p.totalWeeks} weeks</span>
                          <span className="sp-progress-pct">{pct}%</span>
                        </div>
                        <div className="sp-progress-track">
                          <div className={`sp-progress-fill ${pct >= 100 ? "done" : ""}`} style={{ width: `${pct}%` }} />
                        </div>
                        <div className="sp-progress-paid">
                          {fmtCur(p.totalPaid)} of {fmtCur(p.totalReturn)} paid
                        </div>
                      </td>
                      {/* Status */}
                      <td><StatusPill status={p.status} /></td>
                      {/* Next Payout */}
                      <td>
                        <div className="sp-date-main">{fmtDate(p.nextPayoutDate)}</div>
                        <div className="sp-date-label">Next Payout</div>
                      </td>
                      {/* Dates */}
                      <td>
                        <div className="sp-date-main">{fmtDate(p.cycleStart)}</div>
                        <div className="sp-date-label">Started</div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile card list */}
          <div className="sp-cards-list">
            {filtered.length === 0 ? (
              <div className="sp-empty">
                <div className="sp-empty-icon">📈</div>
                <div className="sp-empty-title">No share purchases found</div>
                <div className="sp-empty-sub">
                  {filter === "all" ? "You haven't purchased any shares yet" : `No ${filter} purchases found`}
                </div>
              </div>
            ) : filtered.map(p => {
              const pct = Math.max(0, Math.min(100, Number(p.progressPercentage || 0)));
              return (
                <div key={p._id} className={`sp-purchase-card ${p.status}`}>
                  {/* Card header */}
                  <div className="sp-card-header">
                    <div>
                      <div className="sp-plan-name">{p.planName}</div>
                    </div>
                    <StatusPill status={p.status} />
                  </div>

                  {/* Stats row */}
                  <div className="sp-card-row">
                    <div className="sp-card-item">
                      <div className="sp-card-item-label">Loan Amount</div>
                      <div className="sp-card-item-val">{fmtCur(p.amount)}</div>
                    </div>
                    <div className="sp-card-item">
                      <div className="sp-card-item-label">Total Return</div>
                      <div className="sp-card-item-val" style={{ color: "#059669" }}>{fmtCur(p.totalReturn)}</div>
                      <div className="sp-card-item-sub">{p.roiPercentage}% Yearly</div>
                    </div>
                    <div className="sp-card-item">
                      <div className="sp-card-item-label">Weekly EMI</div>
                      <div className="sp-card-item-val" style={{ color: "#92400e" }}>{fmtCur(p.weeklyPayout)}</div>
                    </div>
                    <div className="sp-card-item">
                      <div className="sp-card-item-label">Next Payout</div>
                      <div className="sp-card-item-val" style={{ fontSize: 13, color: "#3b82f6" }}>{fmtDate(p.nextPayoutDate)}</div>
                    </div>
                    <div className="sp-card-item">
                      <div className="sp-card-item-label">Start Date</div>
                      <div className="sp-card-item-val" style={{ fontSize: 13 }}>{fmtDate(p.cycleStart)}</div>
                    </div>
                    <div className="sp-card-item">
                      <div className="sp-card-item-label">End Date</div>
                      <div className="sp-card-item-val" style={{ fontSize: 13 }}>{fmtDate(p.cycleEnd)}</div>
                    </div>
                  </div>

                  {/* Progress */}
                  <div>
                    <div className="sp-progress-header">
                      <span className="sp-progress-weeks">{p.weeksPaid}/{p.totalWeeks} weeks</span>
                      <span className="sp-progress-pct">{pct}%</span>
                    </div>
                    <div className="sp-progress-track">
                      <div className={`sp-progress-fill ${pct >= 100 ? "done" : ""}`} style={{ width: `${pct}%` }} />
                    </div>
                    <div className="sp-progress-paid">{fmtCur(p.totalPaid)} of {fmtCur(p.totalReturn)} paid</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

      </div>
    </div>
  );
};

/* StatCard Component */
const StatCard = ({ variant, icon, label, value }) => (
  <div className={`sp-stat-card ${variant}`}>
    <div className="sp-stat-top">
      <div className="sp-stat-icon">{icon}</div>
      <div className="sp-stat-value">{value}</div>
    </div>
    <div className="sp-stat-label">{label}</div>
  </div>
);

export default SharePurchases;